import React, { useState } from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Badge,
  Stack,
  Grid,
  Card,
  Avatar,
  Divider,
  ActionIcon,
  LoadingOverlay,
  Alert,
  SimpleGrid,
  Progress,
  Tabs,
  Image,
  FileButton,
} from '@mantine/core';
import {
  IconEdit,
  IconArrowLeft,
  IconUser,
  IconCake,
  IconSchool,
  IconUsers,
  IconActivity,
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconPhoto,
  IconBrain,
  IconUpload,
  IconMedicalCross,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStudent, useUploadStudentPhotos, useTrainFaceRecognition } from '../../hooks/useStudents';
import ActivityTimeline from '../../components/activities/ActivityTimeline';

const StudentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<string>('overview');

  const {
    data: student,
    isLoading: studentLoading,
    error: studentError,
  } = useStudent(id || '');

  const uploadPhotosMutation = useUploadStudentPhotos();
  const trainFaceRecognitionMutation = useTrainFaceRecognition();

  const handleEdit = () => {
    navigate(`/admin/students/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/admin/students');
  };

  const handleUploadPhotos = async (files: File[]) => {
    if (!student || files.length === 0) return;

    try {
      await uploadPhotosMutation.mutateAsync({
        id: student.id,
        photos: files,
      });
    } catch (error) {
      // Error handling: Photo upload failed
    }
  };

  const handleTrainFaceRecognition = async () => {
    if (!student) return;

    try {
      await trainFaceRecognitionMutation.mutateAsync(student.id);
    } catch (error) {
      // Error handling: Face recognition training failed
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      inactive: 'red',
      graduated: 'blue',
    };
    return colors[status] || 'gray';
  };

  const getFaceRecognitionStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      trained: 'green',
      training: 'yellow',
      not_trained: 'gray',
      training_failed: 'red',
    };
    return colors[status] || 'gray';
  };

  const formatAge = (dateOfBirth: string) => {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  if (studentError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error loading student"
        color="red"
      >
        Failed to load student details. Please try again.
      </Alert>
    );
  }

  if (studentLoading || !student) {
    return <LoadingOverlay visible />;
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Group>
          <ActionIcon variant="subtle" onClick={handleBack}>
            <IconArrowLeft size={16} />
          </ActionIcon>
          <div>
            <Title order={2}>{student.first_name} {student.last_name}</Title>
            <Text size="sm" c="dimmed">
              Student Profile
            </Text>
          </div>
        </Group>
        <Button
          leftSection={<IconEdit size={16} />}
          onClick={handleEdit}
        >
          Edit Student
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconUser size={14} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="photos" leftSection={<IconPhoto size={14} />}>
            Photos & Face Recognition
          </Tabs.Tab>
          <Tabs.Tab value="activities" leftSection={<IconActivity size={14} />}>
            Activities
          </Tabs.Tab>
          <Tabs.Tab value="medical" leftSection={<IconMedicalCross size={14} />}>
            Medical Info
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <Grid>
            {/* Main Student Information */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                {/* Basic Info Card */}
                <Card>
                  <Group mb="md">
                    <Avatar size="xl" radius="md">
                      {student.first_name?.[0]}{student.last_name?.[0]}
                    </Avatar>
                    <div style={{ flex: 1 }}>
                      <Group justify="space-between" align="flex-start">
                        <div>
                          <Title order={3}>{student.first_name} {student.last_name}</Title>
                          {student.nickname && (
                            <Text c="dimmed" size="sm">
                              Nickname: "{student.nickname}"
                            </Text>
                          )}
                          <Text c="dimmed" size="sm">
                            Student ID: {student.id}
                          </Text>
                        </div>
                        <Group gap="xs">
                          <Badge
                            color={getStatusColor(student.status || 'enrolled')}
                            variant="light"
                          >
                            {student.status}
                          </Badge>
                          <Badge
                            color={getFaceRecognitionStatusColor(student.face_recognition_status || 'not_trained')}
                            variant="light"
                          >
                            Face: {student.face_recognition_status?.replace('_', ' ') || 'unknown'}
                          </Badge>
                        </Group>
                      </Group>
                    </div>
                  </Group>

                  <Divider mb="md" />

                  <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                    <Group>
                      <IconCake size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Age</Text>
                        <Text size="sm">{student.date_of_birth ? formatAge(student.date_of_birth) : 'Unknown'} years old</Text>
                        <Text size="xs" c="dimmed">
                          Born: {student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString() : 'Unknown'}
                        </Text>
                      </div>
                    </Group>

                    <Group>
                      <IconUser size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Gender</Text>
                        <Text size="sm" tt="capitalize">{student.gender}</Text>
                      </div>
                    </Group>

                    <Group>
                      <IconSchool size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">School</Text>
                        <Text size="sm">{student.school?.name || 'Not assigned'}</Text>
                      </div>
                    </Group>

                    {student.class && (
                      <Group>
                        <IconUsers size={16} color="gray" />
                        <div>
                          <Text size="xs" c="dimmed">Class</Text>
                          <Text size="sm">{student.class.name}</Text>
                        </div>
                      </Group>
                    )}

                    <Group>
                      <IconClock size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Enrolled</Text>
                        <Text size="sm">
                          {student.enrollment_date ? new Date(student.enrollment_date).toLocaleDateString() : 'Unknown'}
                        </Text>
                      </div>
                    </Group>

                    <Group>
                      <IconActivity size={16} color="gray" />
                      <div>
                        <Text size="xs" c="dimmed">Activities</Text>
                        <Text size="sm">{student.activities_count} recorded</Text>
                      </div>
                    </Group>
                  </SimpleGrid>
                </Card>

                {/* Parents Information */}
                <Card>
                  <Title order={4} mb="md">Parent Information</Title>
                  {student.parents && student.parents.length > 0 ? (
                    <Stack gap="md">
                      {student.parents.map((parent: any) => (
                        <Group key={parent.id} gap="sm">
                          <Avatar size="sm" radius="xl">
                            {parent.first_name?.[0]}{parent.last_name?.[0]}
                          </Avatar>
                          <div style={{ flex: 1 }}>
                            <Text size="sm" fw={500}>
                              {parent.first_name} {parent.last_name}
                            </Text>
                            <Group gap="lg">
                              {parent.email && (
                                <Group gap={4}>
                                  <IconMail size={12} />
                                  <Text size="xs">{parent.email}</Text>
                                </Group>
                              )}
                              {parent.phone && (
                                <Group gap={4}>
                                  <IconPhone size={12} />
                                  <Text size="xs">{parent.phone}</Text>
                                </Group>
                              )}
                            </Group>
                          </div>
                        </Group>
                      ))}
                    </Stack>
                  ) : (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No parent information available
                    </Text>
                  )}
                </Card>

                {/* Emergency Contact */}
                {student.emergency_contact && (
                  <Card>
                    <Title order={4} mb="md">Emergency Contact</Title>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <div>
                        <Text size="xs" c="dimmed">Name</Text>
                        <Text size="sm">{student.emergency_contact.name}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Relationship</Text>
                        <Text size="sm">{student.emergency_contact.relationship}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Phone</Text>
                        <Text size="sm">{student.emergency_contact.phone}</Text>
                      </div>
                      {student.emergency_contact.email && (
                        <div>
                          <Text size="xs" c="dimmed">Email</Text>
                          <Text size="sm">{student.emergency_contact.email}</Text>
                        </div>
                      )}
                    </SimpleGrid>
                  </Card>
                )}
              </Stack>
            </Grid.Col>

            {/* Sidebar */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                {/* Quick Stats */}
                <Card>
                  <Title order={4} mb="md">Quick Stats</Title>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconPhoto size={16} color="blue" />
                        <Text size="sm">Photos</Text>
                      </Group>
                      <Text size="sm" fw={500}>
                        {student.photos_count}
                      </Text>
                    </Group>

                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconActivity size={16} color="green" />
                        <Text size="sm">Activities</Text>
                      </Group>
                      <Text size="sm" fw={500}>
                        {student.activities_count}
                      </Text>
                    </Group>

                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconBrain size={16} color="orange" />
                        <Text size="sm">Recognition Confidence</Text>
                      </Group>
                      <Text size="sm" fw={500}>
                        {student.face_recognition_confidence 
                          ? `${(student.face_recognition_confidence * 100).toFixed(1)}%`
                          : 'N/A'
                        }
                      </Text>
                    </Group>
                  </Stack>
                </Card>

                {/* Face Recognition Status */}
                <Card>
                  <Title order={4} mb="md">Face Recognition</Title>
                  <Stack gap="md">
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Status</Text>
                        <Badge
                          color={getFaceRecognitionStatusColor(student.face_recognition_status || 'not_trained')}
                          variant="light"
                          size="sm"
                        >
                          {student.face_recognition_status?.replace('_', ' ') || 'unknown'}
                        </Badge>
                      </Group>
                      {student.face_recognition_confidence && (
                        <Progress
                          value={student.face_recognition_confidence * 100}
                          color={student.face_recognition_confidence > 0.8 ? 'green' : 'yellow'}
                          size="sm"
                        />
                      )}
                    </div>

                    <Group gap="xs">
                      <Text size="sm">Photos: {student.photos_count}</Text>
                      <Text size="sm" c="dimmed">
                        (min. 5 recommended)
                      </Text>
                    </Group>

                    {student.face_recognition_status === 'not_trained' && (student.photos_count || 0) >= 5 && (
                      <Button
                        size="xs"
                        fullWidth
                        leftSection={<IconBrain size={14} />}
                        onClick={handleTrainFaceRecognition}
                        loading={trainFaceRecognitionMutation.isPending}
                      >
                        Train Recognition
                      </Button>
                    )}
                  </Stack>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <Title order={4} mb="md">Recent Activity</Title>
                  <Stack gap="xs">
                    <Group>
                      <IconCheck size={14} color="green" />
                      <Text size="xs" c="dimmed">
                        Student profile viewed
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" ta="center" py="md">
                      More activity data coming soon
                    </Text>
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="photos" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card>
                <Group justify="space-between" mb="md">
                  <Title order={4}>Student Photos</Title>
                  <FileButton
                    onChange={handleUploadPhotos}
                    accept="image/*"
                    multiple
                  >
                    {(props) => (
                      <Button
                        {...props}
                        leftSection={<IconUpload size={16} />}
                        loading={uploadPhotosMutation.isPending}
                      >
                        Upload Photos
                      </Button>
                    )}
                  </FileButton>
                </Group>

                <Text size="sm" c="dimmed" mb="md">
                  Upload at least 5 high-quality photos for optimal face recognition training.
                </Text>

                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
                  {/* Placeholder for actual photos - in real app, you'd fetch from API */}
                  {Array.from({ length: student.photos_count || 0 }).map((_, index) => (
                    <div key={index}>
                      <Image
                        src={null}
                        alt={`Student photo ${index + 1}`}
                        fallbackSrc="https://via.placeholder.com/150x150?text=Photo"
                        radius="md"
                        h={150}
                      />
                    </div>
                  ))}
                  
                  {student.photos_count === 0 && (
                    <Text size="sm" c="dimmed" ta="center" py="xl" style={{ gridColumn: '1 / -1' }}>
                      No photos uploaded yet
                    </Text>
                  )}
                </SimpleGrid>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card>
                <Title order={4} mb="md">Training Tips</Title>
                <Stack gap="xs">
                  <Text size="sm">• Upload 5-10 clear photos</Text>
                  <Text size="sm">• Include different angles and expressions</Text>
                  <Text size="sm">• Ensure good lighting</Text>
                  <Text size="sm">• Avoid sunglasses or hats</Text>
                  <Text size="sm">• Photos should show face clearly</Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="activities" pt="md">
          <ActivityTimeline
            studentId={id!}
            student={student}
            maxItems={20}
            showFilters={true}
            showHeader={false}
          />
        </Tabs.Panel>

        <Tabs.Panel value="medical" pt="md">
          <Card>
            <Title order={4} mb="md">Medical Information</Title>
            {student.medical_notes ? (
              <div>
                <Text size="xs" c="dimmed" mb="xs">Medical Notes</Text>
                <Text size="sm">{student.medical_notes}</Text>
              </div>
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No medical information provided
              </Text>
            )}
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export default StudentDetail;